import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, Eye, Edit3, Mail, Send, X, XCircle, Paperclip } from 'lucide-react';
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { useEmailTemplates } from '../../hooks/useEmailTemplates';
import { notify } from '../../stores/notificationStore';
import { PDFPreview } from '../PDFPreview';

/**
 * Interface pour les donnees d'email
 */
export interface EmailData {
  to: string;
  subject: string;
  body: string;
  templateId?: number;
}

/**
 * Props du modal unifie
 */
interface UnifiedEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (data: EmailData) => Promise<void>;

  // Document info
  documentType: 'quote' | 'invoice' | 'credit-note';
  documentNumber: string;

  // Client info
  clientEmail: string;
  clientName: string;

  // PDF preview (optional)
  pdfBlob?: Blob;
  pdfUrl?: string;
  showPdfPreview?: boolean;

  // Contenu initial pre-rendu (prioritaire sur templates)
  initialSubject?: string;
  initialBody?: string;

  // Template variables (optional, pour changement de template)
  templateVariables?: Record<string, any>;
}

type SendState = 'idle' | 'loading' | 'success' | 'error';

/**
 * Modal d'envoi d'email unifie pour factures et devis
 * Design System V2 - Glassmorphism
 */
export const UnifiedEmailModal: React.FC<UnifiedEmailModalProps> = ({
  isOpen,
  onClose,
  onSend,
  documentType,
  documentNumber,
  clientEmail,
  clientName,
  pdfBlob,
  pdfUrl,
  showPdfPreview = true,
  templateVariables = {},
}) => {
  const { t } = useTranslation();
  const templateCode =
    documentType === 'quote' ? 'quoteSent' :
    documentType === 'invoice' ? 'invoiceSent' :
    'creditNoteSent';
  const { templates, loading: loadingTemplates, renderTemplate } = useEmailTemplates(templateCode);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [formData, setFormData] = useState<EmailData>({
    to: clientEmail,
    subject: '',
    body: '',
  });
  const [sendState, setSendState] = useState<SendState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');

  const shouldShowPdf = showPdfPreview && (pdfBlob || pdfUrl);

  // Construire les variables de template a chaque render (pas de useMemo pour eviter stale refs)
  const buildTemplateVars = useCallback((): Record<string, any> => ({
    clientName: clientName || '',
    quoteNumber: documentType === 'quote' ? documentNumber : '',
    invoiceNumber: documentType === 'invoice' ? documentNumber : '',
    creditNoteNumber: documentType === 'credit-note' ? documentNumber : '',
    ...templateVariables,
  }), [clientName, documentNumber, documentType, templateVariables]);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      const vars = buildTemplateVars();
      const { subject, bodyHtml } = renderTemplate(template, vars);
      setFormData((prev) => ({ ...prev, subject, body: bodyHtml }));
    }
  };

  const handleFieldChange = (field: keyof EmailData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSend = async () => {
    if (!formData.to.trim()) {
      notify.error(t('common.email.recipientLabel'), t('common.error'));
      return;
    }

    if (!formData.subject.trim()) {
      notify.error(t('common.email.subjectLabel'), t('common.error'));
      return;
    }

    if (!formData.body.trim()) {
      notify.error(t('common.email.messageLabel'), t('common.error'));
      return;
    }

    setSendState('loading');
    setErrorMessage(null);

    try {
      await onSend({
        ...formData,
        templateId: selectedTemplateId ? Number(selectedTemplateId) : undefined,
      });

      setSendState('success');
      notify.success(t('common.email.documentSentTo', { document: documentType === 'quote' ? t('common.email.quote') : t('common.email.invoice'), email: formData.to }), t('common.email.emailSent'));

      setTimeout(() => {
        onClose();
        setTimeout(() => {
          setSendState('idle');
          setErrorMessage(null);
        }, 300);
      }, 2000);
    } catch (error) {
      setSendState('error');
      const message = error instanceof Error ? error.message : t('common.email.sendError');
      setErrorMessage(message);
      notify.error(message, t('common.email.sendError'));
    }
  };

  // Formater un montant en euros
  const formatAmount = useCallback((value: any): string => {
    if (!value && value !== 0) return '';
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (isNaN(num)) return String(value);
    return `${num.toFixed(2)} €`;
  }, []);

  // Formater une date en francais
  const formatDate = useCallback((value: any): string => {
    if (!value) return '';
    // Si c'est deja formate (contient des lettres), retourner tel quel
    if (typeof value === 'string' && /[a-zA-Zéèêàùû]/.test(value)) return value;
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return String(value);
    }
  }, []);

  // Generer le contenu email directement depuis les props (JS template literals)
  const generateEmailContent = useCallback((): { subject: string; body: string } => {
    const v = {
      clientName: clientName || '',
      total: formatAmount(templateVariables.total),
      dueDate: formatDate(templateVariables.dueDate),
      companyName: templateVariables.companyName || '',
      paymentConditions: templateVariables.paymentConditions || 'À réception de facture',
      publicLink: templateVariables.publicLink || '',
    };

    if (documentType === 'invoice') {
      return {
        subject: `Facture ${documentNumber} - ${v.companyName}`,
        body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <p>Bonjour ${v.clientName},</p>
  <p>Veuillez trouver ci-joint la facture <strong>${documentNumber}</strong> d'un montant de <strong>${v.total}</strong>${v.dueDate ? `, avec une date d'échéance au <strong>${v.dueDate}</strong>` : ''}.</p>
  <p>Vous pouvez également consulter et télécharger votre facture en ligne :</p>
  ${v.publicLink ? `<p><a href="${v.publicLink}" style="display: inline-block; padding: 10px 24px; background-color: #0d9488; color: white; text-decoration: none; border-radius: 8px; font-weight: 600;">Voir la facture</a></p>` : ''}
  <p>Conditions de règlement : ${v.paymentConditions}</p>
  <p>Cordialement,<br/><strong>${v.companyName}</strong></p>
</div>`,
      };
    }

    if (documentType === 'quote') {
      return {
        subject: `Devis ${documentNumber} - ${v.companyName}`,
        body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <p>Bonjour ${v.clientName},</p>
  <p>Veuillez trouver ci-joint le devis <strong>${documentNumber}</strong> d'un montant de <strong>${v.total}</strong>.</p>
  <p>Vous pouvez également consulter votre devis en ligne et l'accepter directement :</p>
  ${v.publicLink ? `<p><a href="${v.publicLink}" style="display: inline-block; padding: 10px 24px; background-color: #0d9488; color: white; text-decoration: none; border-radius: 8px; font-weight: 600;">Voir le devis</a></p>` : ''}
  <p>N'hésitez pas à nous contacter pour toute question.</p>
  <p>Cordialement,<br/><strong>${v.companyName}</strong></p>
</div>`,
      };
    }

    // credit-note
    return {
      subject: `Avoir ${documentNumber} - ${v.companyName}`,
      body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <p>Bonjour ${v.clientName},</p>
  <p>Veuillez trouver ci-joint l'avoir <strong>${documentNumber}</strong> d'un montant de <strong>${v.total}</strong>.</p>
  <p>Vous pouvez également consulter votre avoir en ligne :</p>
  ${v.publicLink ? `<p><a href="${v.publicLink}" style="display: inline-block; padding: 10px 24px; background-color: #0d9488; color: white; text-decoration: none; border-radius: 8px; font-weight: 600;">Voir l'avoir</a></p>` : ''}
  <p>Cordialement,<br/><strong>${v.companyName}</strong></p>
</div>`,
    };
  }, [clientName, documentType, documentNumber, templateVariables, formatAmount, formatDate]);

  // Reset + initialisation quand le modal s'ouvre
  useEffect(() => {
    if (!isOpen) return;

    setSendState('idle');
    setErrorMessage(null);
    setViewMode('edit');

    // Generer le contenu directement depuis les props (pas de {{variables}})
    const { subject, body } = generateEmailContent();
    setFormData({ to: clientEmail, subject, body });

    // Selectionner le premier template dans la liste (pour l'affichage)
    if (templates.length > 0) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [isOpen, clientEmail, generateEmailContent, templates]);

  const documentLabel = documentType === 'quote' ? t('common.email.theQuote') : documentType === 'credit-note' ? "l'avoir" : t('common.email.theInvoice');
  const documentLabelCap = documentType === 'quote' ? t('common.email.quote') : documentType === 'credit-note' ? 'Avoir' : t('common.email.invoice');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - Glassmorphism */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          />

          {/* Modal - Design System V2 Glassmorphism */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed inset-4 md:inset-8 lg:inset-12 bg-white/80 backdrop-blur-2xl rounded-3xl shadow-2xl z-50 flex flex-col overflow-hidden border border-white/30"
          >
            {/* Header - Gradient teal */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">
                    {t('common.email.sendDocument', { document: documentLabel })}
                  </h2>
                  <p className="text-sm text-teal-100">{t('common.email.documentNumber', { number: documentNumber })}</p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                disabled={sendState === 'loading'}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
              {/* Left: PDF Preview (optional) */}
              {shouldShowPdf && (
                <div className="flex-1 p-6 border-r border-gray-200/50 overflow-auto bg-gray-50/30">
                  <PDFPreview
                    pdfBlob={pdfBlob}
                    pdfUrl={pdfUrl}
                    title={`${documentLabelCap} ${documentNumber}`}
                    className="h-full"
                    showDownload={false}
                  />
                </div>
              )}

              {/* Right: Email Form */}
              <div className={`${shouldShowPdf ? 'flex-1' : 'flex-1 max-w-3xl mx-auto w-full'} flex flex-col overflow-hidden`}>
                <div className="flex-1 p-6 overflow-auto">
                  {/* Success State */}
                  {sendState === 'success' && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="flex flex-col items-center justify-center h-full text-center"
                    >
                      <div className="p-4 bg-emerald-100 rounded-full mb-4">
                        <CheckCircle className="w-16 h-16 text-emerald-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('common.email.emailSent')}</h3>
                      <p className="text-gray-600">
                        {t('common.email.documentSentTo', { document: documentLabelCap, email: formData.to })}
                      </p>
                    </motion.div>
                  )}

                  {/* Error State */}
                  {sendState === 'error' && (
                    <div className="mb-4 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200/60 rounded-2xl flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-900">{t('common.email.sendError')}</p>
                        <p className="text-sm text-red-700">{errorMessage}</p>
                      </div>
                    </div>
                  )}

                  {/* Form */}
                  {sendState !== 'success' && (
                    <div className="space-y-5">
                      {/* Mode Toggle: Edit / Preview */}
                      <div className="flex items-center gap-1 p-1 bg-gray-100/80 backdrop-blur-sm rounded-xl w-fit border border-gray-200/50">
                        <button
                          onClick={() => setViewMode('edit')}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                            viewMode === 'edit'
                              ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Edit3 className="w-4 h-4" />
                          {t('common.email.editMode')}
                        </button>
                        <button
                          onClick={() => setViewMode('preview')}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                            viewMode === 'preview'
                              ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Eye className="w-4 h-4" />
                          {t('common.email.previewMode')}
                        </button>
                      </div>

                      {/* Edit Mode */}
                      {viewMode === 'edit' && (
                        <>
                          {/* Template Selector */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              {t('common.email.templateLabel')}
                            </label>
                            <select
                              value={selectedTemplateId || ''}
                              onChange={(e) => handleTemplateChange(e.target.value)}
                              disabled={loadingTemplates || sendState === 'loading'}
                              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/60 rounded-xl focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-all disabled:opacity-50 text-gray-900"
                            >
                              {loadingTemplates ? (
                                <option>{t('common.email.loadingTemplates')}</option>
                              ) : templates.length === 0 ? (
                                <option>{t('common.email.noTemplateAvailable')}</option>
                              ) : (
                                templates.map((template) => (
                                  <option key={template.id} value={template.id}>
                                    {template.name}
                                  </option>
                                ))
                              )}
                            </select>
                          </div>

                          {/* Recipient Email */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              {t('common.email.recipientLabel')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              value={formData.to}
                              onChange={(e) => handleFieldChange('to', e.target.value)}
                              disabled={sendState === 'loading'}
                              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/60 rounded-xl focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-all disabled:opacity-50 text-gray-900 placeholder:text-gray-400"
                              placeholder="client@example.com"
                            />
                          </div>

                          {/* Subject */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              {t('common.email.subjectLabel')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.subject}
                              onChange={(e) => handleFieldChange('subject', e.target.value)}
                              disabled={sendState === 'loading'}
                              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/60 rounded-xl focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-all disabled:opacity-50 text-gray-900 placeholder:text-gray-400"
                              placeholder={t('common.email.subjectPlaceholder')}
                            />
                          </div>

                          {/* Message Body */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              {t('common.email.messageLabel')} <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              value={formData.body}
                              onChange={(e) => handleFieldChange('body', e.target.value)}
                              disabled={sendState === 'loading'}
                              rows={10}
                              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/60 rounded-xl focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-all resize-none disabled:opacity-50 text-gray-900 placeholder:text-gray-400"
                              placeholder={t('common.email.messagePlaceholder')}
                            />
                            <p className="mt-1.5 text-xs text-gray-500">
                              {documentType === 'invoice'
                                ? t('common.email.invoiceAttachmentNote')
                                : t('common.email.quoteAttachmentNote')}
                            </p>
                          </div>

                          {/* Attachment indicator */}
                          <div className="flex items-center gap-2.5 p-3 bg-teal-50/60 backdrop-blur-sm border border-teal-200/40 rounded-xl">
                            <Paperclip className="w-4 h-4 text-teal-600" />
                            <span className="text-sm text-teal-800 font-medium">
                              {documentType === 'invoice'
                                ? `facture-${documentNumber}.pdf (Factur-X)`
                                : `${documentType === 'quote' ? 'devis' : 'avoir'}-${documentNumber}.pdf`}
                            </span>
                          </div>

                          {/* Info Factur-X pour les factures */}
                          {documentType === 'invoice' && (
                            <div className="bg-blue-50/60 backdrop-blur-sm border border-blue-200/40 rounded-xl p-4">
                              <p className="text-sm text-blue-800">
                                <strong>Info :</strong> {t('common.email.facturxInfo')}
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {/* Preview Mode */}
                      {viewMode === 'preview' && (
                        <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/50 overflow-hidden shadow-sm">
                          {/* Email Header Preview */}
                          <div className="bg-white/80 border-b border-gray-200/50 p-4 space-y-3">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-gray-500 w-16">{t('common.email.from')}</span>
                              <span className="text-sm text-gray-900">votre-entreprise@example.com</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-gray-500 w-16">{t('common.email.to')}</span>
                              <span className="text-sm text-gray-900">{formData.to || 'destinataire@example.com'}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-gray-500 w-16">{t('common.email.subject')}</span>
                              <span className="text-sm font-bold text-gray-900">{formData.subject || t('common.email.noSubject')}</span>
                            </div>
                            <div className="flex items-center gap-3 pt-2 border-t border-gray-100/50">
                              <Paperclip className="w-4 h-4 text-teal-500" />
                              <span className="text-sm text-teal-700 font-medium">
                                {documentType === 'invoice'
                                  ? `facture-${documentNumber}.pdf (Factur-X)`
                                  : `${documentType === 'quote' ? 'devis' : 'avoir'}-${documentNumber}.pdf`}
                              </span>
                            </div>
                          </div>

                          {/* Email Body Preview */}
                          <div className="p-6 bg-white/90 min-h-[250px]">
                            {formData.body ? (
                              <div
                                className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-teal-600"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.body) }}
                              />
                            ) : (
                              <div className="flex items-center justify-center h-40 text-gray-400">
                                {t('common.email.noContentToPreview')}
                              </div>
                            )}
                          </div>

                          {/* Footer Note */}
                          <div className="bg-gray-50/50 border-t border-gray-200/50 px-4 py-3">
                            <p className="text-xs text-gray-500 text-center">
                              {t('common.email.previewDisclaimer')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer Actions */}
                {sendState !== 'success' && (
                  <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200/50 bg-gray-50/50 backdrop-blur-sm">
                    <button
                      onClick={onClose}
                      disabled={sendState === 'loading'}
                      className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100/80 rounded-xl transition-all disabled:opacity-50 border border-gray-200/60"
                    >
                      {t('common.actions.cancel')}
                    </button>

                    <button
                      onClick={handleSend}
                      disabled={sendState === 'loading'}
                      className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold text-sm transition-all shadow-lg shadow-teal-600/25 hover:shadow-xl hover:shadow-teal-600/30 disabled:opacity-50"
                    >
                      {sendState === 'loading' ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                          {t('common.email.sending')}
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          {t('common.email.send')}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
